import { useEffect, useState } from 'react';
import { Loader2, Zap } from 'lucide-react';

interface LoadingPageProps {
  message?: string;
  showProgress?: boolean;
  timeout?: number;
  onTimeout?: () => void;
}

export default function LoadingPage({ 
  message = 'Loading...', 
  showProgress = false,
  timeout,
  onTimeout
}: LoadingPageProps) {
  const [progress, setProgress] = useState(0);
  const [dots, setDots] = useState('');

  // Animated dots effect
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev.length >= 3) return '';
        return prev + '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Progress simulation
  useEffect(() => {
    if (!showProgress) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 200);

    return () => clearInterval(interval);
  }, [showProgress]);

  // Timeout handler
  useEffect(() => {
    if (!timeout) return;

    const timer = setTimeout(() => {
      onTimeout?.();
    }, timeout);

    return () => clearTimeout(timer);
  }, [timeout, onTimeout]);

  return (
    <div className="min-h-screen bg-oro-bg flex items-center justify-center p-4">
      <div className="text-center max-w-sm mx-auto">
        {/* Loading Animation */}
        <div className="relative mb-8">
          {/* Outer ring */}
          <div className="w-20 h-20 mx-auto relative">
            <div className="absolute inset-0 border-4 border-oro-surface-alt rounded-full"></div>
            <div className="absolute inset-0 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
            
            {/* Inner icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Zap className="w-8 h-8 text-accent" />
            </div>
          </div>

          {/* Pulse effect */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 bg-accent/10 rounded-full animate-ping"></div>
          </div>
        </div>

        {/* Loading Text */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            {message}
            <span className="inline-block w-8 text-left text-accent">{dots}</span>
          </h2>
          
          <p className="text-sm text-text-muted">
            This won't take long
          </p>
        </div>

        {/* Progress Bar */}
        {showProgress && (
          <div className="mb-6">
            <div className="bg-oro-surface-alt rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-accent to-accent-light transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-text-muted mt-2">
              {Math.round(progress)}%
            </p>
          </div>
        )}

        {/* Loading States */}
        <div className="space-y-2">
          <div className="flex items-center justify-center gap-2 text-xs text-text-muted">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Connecting to server</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12">
          <div className="flex items-center justify-center gap-1 text-xs text-text-muted">
            <span>Powered by</span>
            <Zap className="w-3 h-3 text-accent" />
            <span className="font-medium text-accent">ORO</span>
          </div>
        </div>
      </div>
    </div>
  );
}